/**
 * O conserto de um compromisso, onde quer que a mãe veja o erro.
 *
 * Mora fora das telas porque duas precisam dele: a de Compromissos, onde ela
 * revê o que está marcado, e a Semana, onde ela **vê** a aula no dia errado. A
 * regra 7 do CLAUDE.md manda o conserto caber na tela onde o erro aparece, e
 * duas cópias do mesmo formulário são duas cópias que divergem.
 */

import { useState, type FormEvent } from 'react'

import { fraseDoErro, type CompromissoMarcado, type CorrecaoDeCompromisso } from '../api'
import '../estilos/conserto.css'

const DIAS = [
  { codigo: 'seg', curto: 'seg' },
  { codigo: 'ter', curto: 'ter' },
  { codigo: 'qua', curto: 'qua' },
  { codigo: 'qui', curto: 'qui' },
  { codigo: 'sex', curto: 'sex' },
  { codigo: 'sab', curto: 'sáb' },
  { codigo: 'dom', curto: 'dom' },
]

/**
 * O conserto inteiro de um compromisso: tirar só um dia, ou corrigir a série.
 *
 * São duas coisas diferentes e a mãe não deveria ter que saber disso. "Amanhã é
 * feriado, as crianças não têm aula" não é corrigir a aula — a aula está certa,
 * é a terça-feira que não tem. Por isso o "nesse dia não tem" vem antes do
 * formulário, e só aparece quando a coisa se repete: num compromisso de uma vez
 * só, tirar o dia é desmarcar, e desmarcar tem outro lugar.
 */
export function ConsertoDoCompromisso({
  compromisso,
  data,
  aoSalvar,
  aoPular,
  aoFechar,
}: {
  compromisso: CompromissoMarcado
  /** O dia em que ela está vendo isto — é esse que o "não tem" tira. */
  data: string
  aoSalvar: (campos: CorrecaoDeCompromisso) => Promise<void>
  aoPular: (dia: string) => Promise<void>
  aoFechar: () => void
}) {
  return (
    <>
      {compromisso.repete_dias.length > 0 && (
        <div className="semana__conserto">
          <p className="semana__conserto-nota">
            Isso se repete. Se nesse dia não tem — feriado, viagem —, eu tiro só esse dia.
          </p>
          <div className="semana__conserto-acoes">
            <button
              type="button"
              className="conf-pessoa__deixar"
              onClick={() => void aoPular(data)}
            >
              Nesse dia não tem
            </button>
          </div>
        </div>
      )}

      <CorrecaoDoCompromisso
        compromisso={compromisso}
        aoSalvar={aoSalvar}
        aoFechar={aoFechar}
      />
    </>
  )
}

/**
 * O formulário de correção, já preenchido com o que a Ruth entendeu.
 *
 * Vem preenchido de propósito: quase sempre ela só quer mexer numa coisa, e
 * um formulário em branco a obrigaria a redigitar o que já estava certo.
 *
 * Manda só o que ela mudou. Sem isso, abrir e salvar sem tocar em nada
 * reescreveria campos que a Ruth tinha preenchido bem.
 */
export function CorrecaoDoCompromisso({
  compromisso,
  aoSalvar,
  aoFechar,
}: {
  compromisso: CompromissoMarcado
  aoSalvar: (campos: CorrecaoDeCompromisso) => Promise<void>
  aoFechar: () => void
}) {
  const [titulo, setTitulo] = useState(compromisso.titulo)
  const [hora, setHora] = useState(compromisso.hora.slice(0, 5))
  const [local, setLocal] = useState(compromisso.local ?? '')
  const [dias, setDias] = useState<string[]>(compromisso.repete_dias)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const repetia = compromisso.repete_dias.length > 0

  const salvar = async (evento: FormEvent) => {
    evento.preventDefault()
    if (salvando) return

    const campos: CorrecaoDeCompromisso = {}
    if (titulo.trim() && titulo.trim() !== compromisso.titulo) campos.titulo = titulo.trim()
    if (`${hora}:00` !== compromisso.hora) campos.hora = `${hora}:00`
    if ((local.trim() || null) !== compromisso.local) campos.local = local.trim() || null
    if (repetia && dias.join(',') !== compromisso.repete_dias.join(',')) campos.repete_dias = dias

    if (Object.keys(campos).length === 0) {
      aoFechar()
      return
    }

    setSalvando(true)
    setErro(null)
    try {
      await aoSalvar(campos)
    } catch (e) {
      setErro(fraseDoErro(e, 'Não consegui salvar agora.'))
      setSalvando(false)
    }
  }

  return (
    <form className="agenda__corrigindo" onSubmit={salvar}>
      <label className="agenda__campo">
        <span>O que é</span>
        <input value={titulo} onChange={(e) => setTitulo(e.target.value)} maxLength={160} />
      </label>

      <label className="agenda__campo">
        <span>Hora</span>
        <input type="time" value={hora} onChange={(e) => setHora(e.target.value)} />
      </label>

      <label className="agenda__campo">
        <span>Onde</span>
        <input
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          placeholder="deixe vazio se não tem lugar"
          maxLength={200}
        />
      </label>

      {repetia && (
        <fieldset className="agenda__dias">
          {/* Diz o alcance porque o formulário abre num cartão só, e o cartão é
              de uma data. Sem esta frase ela mudaria a hora olhando para a
              terça e descobriria a quinta mudada junto. */}
          <legend>Em que dias — isto vale para todas as vezes</legend>
          {DIAS.map(({ codigo, curto }) => (
            <label key={codigo} className="agenda__dia-escolha">
              <input
                type="checkbox"
                checked={dias.includes(codigo)}
                onChange={(e) =>
                  setDias((antes) =>
                    e.target.checked ? [...antes, codigo] : antes.filter((d) => d !== codigo),
                  )
                }
              />
              {curto}
            </label>
          ))}
        </fieldset>
      )}

      {erro && (
        <p className="agenda__erro" role="alert">
          {erro}
        </p>
      )}

      <div className="agenda__acoes">
        <button type="submit" className="confirmar__sim" disabled={salvando}>
          {salvando ? 'Salvando…' : 'Salvar'}
        </button>
        <button type="button" className="confirmar__nao" onClick={aoFechar}>
          Deixa como está
        </button>
      </div>
    </form>
  )
}
